import { useState } from "react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "@/hooks/redux-hooks";
import { Link } from "react-router-dom";
import GoogleBtn from "@components/auth/google.button";
import { login } from "@/store/reducers/auth.reducer";
import loginSchema from "./login.schema";
import { Button } from "@components/ui/button";
import { PATH_FORGOT_PASSWORD } from "@/routes/paths";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { AxiosError } from "axios";


type Inputs = {
  email: string;
  password: string;
};

const LoginForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { status } = useAppSelector((s) => s.auth);
  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm<Inputs>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const { control, handleSubmit } = methods;

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      await dispatch(login(data)).unwrap();
      enqueueSnackbar("Login Successfully", {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      // If server responded with status code (401, 400, etc.), it's NOT a network error
      const hasServerResponse = axiosError?.response || axiosError?.status;
      
      // Check if it's a network error (no response from server)
      const isNetworkError = 
        !navigator.onLine || 
        axiosError?.code === 'ERR_NETWORK' ||
        axiosError?.message === 'Network Error' ||
        (!hasServerResponse && (axiosError?.message?.toLowerCase().includes('network') || axiosError?.message?.toLowerCase().includes('timeout')));
      
      if (isNetworkError) {
        enqueueSnackbar("No internet connection. Please check your network and try again.", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
      } else {
        enqueueSnackbar("Invalid Login", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
      }
    }
  };

 

  return (
    <Form {...methods}>
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <FormField
          control={control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-800">Email <span className="text-red-500">*</span></FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter your Email"
                  className="bg-white text-black border-gray-400"
                  autoComplete="password"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-xs text-red-500"/>
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-800">Password <span className="text-red-500">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className="bg-white border-gray-400 text-gray-800 pr-10"
                    autoComplete="current-password"
                    {...field}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </FormControl>
              <FormMessage className="text-xs text-red-500"/>
            </FormItem>
          )}
        />

        <Link className="text-xs inline-block text-right mt-2 text-[#3758F9]" to={PATH_FORGOT_PASSWORD}>
          Forgot password?
        </Link>

        <Button className="w-full bg-sky-600 hover:bg-sky-800" type="submit" disabled={status === "PENDING"}>
          Log In
        </Button>

        <div className="font-bold my-5 text-center text-gray-800">OR</div>

        <GoogleBtn />
      </form>
    </Form>
  );
};

export default LoginForm;
